import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { useTranslation }    from '../../i18n/LanguageContext'
import { useSymmetry, type SymmetryMode, type Difficulty } from './useSymmetry'
import { useTimer }           from '../useTimer'
import { useGameCompletion }  from '../../hooks/useGameCompletion'
import { useGameSession }     from '../../hooks/useGameSession'
import { useActivitySession } from '../../hooks/useActivitySession'
import { useSessionMode }     from '../../professional/SessionModeContext'
import GameSetupScreen        from '../GameSetupScreen'
import GameCompleteScreen     from '../GameCompleteScreen'
import SaveErrorBanner        from '../SaveErrorBanner'
import { ss }                 from '../gameSetupStyles'
import GamePlayHeader         from '../GamePlayHeader'
import { COLORS, RADIUS, FONT } from '../../styles/tokens'
import { formatGameTime }     from '../utils'

// ── Color labels for accessibility and UI ──────────────────────────────────

const COLOR_NAMES: Record<string, string> = {
  '#000000': 'Negro',
  '#FFFFFF': 'Blanco',
  '#1B4332': 'Verde',
  '#2E4CB8': 'Azul',
  '#7B2D8E': 'Morado',
  '#D4A017': 'Dorado',
  '#C0392B': 'Rojo',
  '#E8751A': 'Naranja',
}

// ── Types ──────────────────────────────────────────────────────────────────

type GameScreen  = 'setup' | 'playing' | 'complete'
type GameType    = 'symmetry' | 'translation'
interface Config { mode: SymmetryMode; difficulty: Difficulty }

const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']

const SYMMETRY_AXES: SymmetryMode[]    = ['vertical', 'horizontal', 'quad']
const TRANSLATION_AXES: SymmetryMode[] = ['translation-vertical', 'translation-horizontal']

function getModeType(mode: SymmetryMode): GameType {
  return mode.startsWith('translation') ? 'translation' : 'symmetry'
}

function getAxisForType(type: GameType, currentMode: SymmetryMode): SymmetryMode {
  if (type === 'translation') {
    if (currentMode === 'horizontal') return 'translation-horizontal'
    return 'translation-vertical'
  }
  if (currentMode === 'translation-vertical')   return 'vertical'
  if (currentMode === 'translation-horizontal') return 'horizontal'
  return currentMode
}

// ── Root ──────────────────────────────────────────────────────────────────

export default function SymmetryGame({ onBack }: { onBack: () => void }) {
  const { t }       = useTranslation()
  const sessionMode = useSessionMode()
  const { savedSession, saveConfig, clear } = useActivitySession<Config>(
    'symmetry',
    !sessionMode,
  )
  const restored = savedSession?.config ?? null

  const [screen,     setScreen]     = useState<GameScreen>(
    sessionMode || restored ? 'playing' : 'setup',
  )
  const [mode,       setMode]       = useState<SymmetryMode>(
    (sessionMode?.config.mode as SymmetryMode) ?? restored?.mode ?? 'vertical',
  )
  const [difficulty, setDifficulty] = useState<Difficulty>(
    (sessionMode?.config.difficulty as Difficulty) ?? restored?.difficulty ?? 'easy',
  )

  const hook = useSymmetry(difficulty, mode)
  const { gameComplete, moves, startTime, endTime, solution, isGiven } = hook.state

  const totalToFill = useMemo(
    () => isGiven.flat().filter((g: boolean) => !g).length,
    [isGiven],
  )

  const { sessionId } = useGameSession({
    activitySlug: 'symmetry',
    difficulty,
    mode,
    enabled: screen === 'playing',
  })

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete,
    activitySlug: 'symmetry',
    difficulty,
    moves,
    startTime,
    endTime,
    pairsTotal:   totalToFill,
    pairsMatched: totalToFill,
    mode,
    sessionId,
    onCompleted: () => { clear(); setScreen('complete') },
  })

  const handleStart = () => {
    hook.resetGame()
    saveConfig({ mode, difficulty })
    setScreen('playing')
  }

  const handlePlayAgain = () => {
    hook.resetGame()
    saveConfig({ mode, difficulty })
    setScreen('playing')
  }

  return (
    <div style={s.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}

      {screen === 'setup' && (
        <SetupScreen
          mode={mode}
          onModeChange={setMode}
          difficulty={difficulty}
          onDifficultyChange={setDifficulty}
          onStart={handleStart}
          onBack={onBack}
        />
      )}

      {screen === 'playing' && (
        <PlayScreen
          state={hook.state}
          mode={mode}
          solution={solution}
          onPaintCell={hook.paintCell}
          onSelectColor={hook.selectColor}
          onBack={sessionMode ? onBack : () => { clear(); setScreen('setup') }}
        />
      )}

      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="🪞"
          title={t.symmetry.complete.title}
          subtitle={t.symmetry.complete.subtitle}
          stats={[
            { value: String(moves),                         label: t.symmetry.complete.movesLabel },
            { value: formatGameTime(startTime, endTime),    label: t.symmetry.complete.timeLabel  },
            { value: t.symmetry.difficulty[difficulty].label, label: t.symmetry.complete.levelLabel },
          ]}
          playAgainLabel={t.symmetry.complete.playAgain}
          changeLevelLabel={t.symmetry.complete.changeLevel}
          backLabel={t.symmetry.complete.backToDashboard}
          onPlayAgain={handlePlayAgain}
          onChangeLevel={() => { clear(); setScreen('setup') }}
          onBack={onBack}
        />
      )}
    </div>
  )
}

// ── Setup Screen ──────────────────────────────────────────────────────────

function SetupScreen({ mode, onModeChange, difficulty, onDifficultyChange, onStart, onBack }: {
  mode:               SymmetryMode
  onModeChange:       (m: SymmetryMode) => void
  difficulty:         Difficulty
  onDifficultyChange: (d: Difficulty) => void
  onStart:            () => void
  onBack:             () => void
}) {
  const { t }  = useTranslation()
  const sy     = t.symmetry
  const gameType = getModeType(mode)
  const axes   = gameType === 'symmetry' ? SYMMETRY_AXES : TRANSLATION_AXES

  const handleTypeChange = useCallback((type: GameType) => {
    onModeChange(getAxisForType(type, mode))
  }, [mode, onModeChange])

  return (
    <GameSetupScreen
      title={sy.title}
      subtitle={sy.subtitle}
      backLabel={sy.backToDashboard}
      onBack={onBack}
      difficulties={DIFFICULTIES.map((id) => ({ id, ...sy.difficulty[id] }))}
      selectedDifficulty={difficulty}
      onDifficultyChange={(id) => onDifficultyChange(id as Difficulty)}
      chooseDifficultyLabel={sy.chooseDifficultyTitle}
      startLabel={sy.startBtn}
      onStart={onStart}
    >
      {/* Type selector: Symmetry vs Translation */}
      <section>
        <h2 style={ss.optionLabel}>{sy.chooseTypeTitle}</h2>
        <div style={ss.modeGrid}>
          {(['symmetry', 'translation'] as GameType[]).map((type) => (
            <button
              key={type}
              onClick={() => handleTypeChange(type)}
              style={{ ...ss.modeCard, ...(gameType === type ? ss.modeCardActive : {}) }}
              aria-pressed={gameType === type}
            >
              <span style={s.modeLabel}>{sy.types[type].label}</span>
              <span style={s.modeDesc}>{sy.types[type].description}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Axis selector */}
      <section>
        <h2 style={ss.optionLabel}>{sy.chooseModeTitle}</h2>
        <div style={ss.modeGrid}>
          {axes.map((m) => (
            <button
              key={m}
              onClick={() => onModeChange(m)}
              style={{ ...ss.modeCard, ...(mode === m ? ss.modeCardActive : {}) }}
              aria-pressed={mode === m}
            >
              <span style={s.modeLabel}>{sy.modes[m].label}</span>
              <span style={s.modeDesc}>{sy.modes[m].description}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Mini preview — key forces remount on mode change, resetting animation phase */}
      <MiniPreview key={mode} mode={mode} />
    </GameSetupScreen>
  )
}

// ── Mini Preview ──────────────────────────────────────────────────────────

function previewCellColor(r: number, c: number, h: number, mode: SymmetryMode): string {
  // Returns the "solution" color for any cell in the 8×8 preview grid.
  // Base pattern in the given half: checkerboard of '#1B4332' and '#FFFFFF'
  const baseColor = (r + c) % 3 !== 0 ? '#1B4332' : '#FFFFFF'

  if (mode === 'vertical') {
    const srcC = c < h ? c : (h - 1 - (c - h))  // mirror c
    return (r + srcC) % 3 !== 0 ? '#1B4332' : '#FFFFFF'
  }
  if (mode === 'horizontal') {
    const srcR = r < h ? r : (h - 1 - (r - h))  // mirror r
    return (srcR + c) % 3 !== 0 ? '#1B4332' : '#FFFFFF'
  }
  if (mode === 'quad') {
    const srcR = r < h ? r : (h - 1 - (r - h))
    const srcC = c < h ? c : (h - 1 - (c - h))
    return (srcR + srcC) % 3 !== 0 ? '#1B4332' : '#FFFFFF'
  }
  if (mode === 'translation-vertical') {
    const srcC = c < h ? c : c - h  // copy: same offset in left half
    return (r + srcC) % 3 !== 0 ? '#1B4332' : '#FFFFFF'
  }
  if (mode === 'translation-horizontal') {
    const srcR = r < h ? r : r - h  // copy: same offset in top half
    return (srcR + c) % 3 !== 0 ? '#1B4332' : '#FFFFFF'
  }
  return baseColor
}

function MiniPreview({ mode }: { mode: SymmetryMode }) {
  const PREVIEW_SIZE = 8
  const CELL         = 16
  const h            = PREVIEW_SIZE / 2

  // Animate between "blank" (phase 0) and "solved" (phase 1).
  // key={mode} on the parent call site forces remount on mode change,
  // which resets this state to 0 without needing setState inside the effect.
  const [phase, setPhase] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setPhase((p) => (p === 0 ? 1 : 0)), 1400)
    return () => clearInterval(id)
  }, [])

  const isGivenCell = useCallback((r: number, c: number): boolean => {
    if (mode === 'vertical'   || mode === 'translation-vertical')   return c < h
    if (mode === 'horizontal' || mode === 'translation-horizontal') return r < h
    return r < h && c < h  // quad
  }, [mode, h])

  const isAxisV = mode !== 'horizontal' && mode !== 'translation-horizontal'
  const isAxisH = mode !== 'vertical'   && mode !== 'translation-vertical'

  return (
    <div style={s.previewWrap}>
      <div
        style={{
          display:             'grid',
          gridTemplateColumns: `repeat(${PREVIEW_SIZE}, ${CELL}px)`,
          gap:                 1,
          border:              `2px solid ${COLORS.borderWarm}`,
          borderRadius:        RADIUS.sm,
          padding:             4,
          background:          COLORS.bgCard,
        }}
      >
        {Array.from({ length: PREVIEW_SIZE * PREVIEW_SIZE }, (_, i) => {
          const r     = Math.floor(i / PREVIEW_SIZE)
          const c     = i % PREVIEW_SIZE
          const given = isGivenCell(r, c)
          const solColor = previewCellColor(r, c, h, mode)

          const showAxisV = isAxisV && c === h
          const showAxisH = isAxisH && r === h

          // Given cells always show. Non-given reveal in phase 1.
          const bg = given
            ? solColor
            : phase === 1 ? solColor : '#FFFFFF'

          return (
            <div
              key={i}
              style={{
                width:       CELL,
                height:      CELL,
                background:  bg,
                borderLeft:  showAxisV ? `3px solid ${COLORS.greenLight}` : undefined,
                borderTop:   showAxisH ? `3px solid ${COLORS.greenLight}` : undefined,
                opacity:     given ? 1 : (phase === 1 ? 0.85 : 0.2),
                transition:  'background 0.5s ease, opacity 0.5s ease',
                boxSizing:   'border-box',
              }}
            />
          )
        })}
      </div>
    </div>
  )
}

// ── Play Screen ───────────────────────────────────────────────────────────

function PlayScreen({
  state, mode, solution, onPaintCell, onSelectColor, onBack,
}: {
  state:         ReturnType<typeof useSymmetry>['state']
  mode:          SymmetryMode
  solution:      string[][]
  onPaintCell:   (r: number, c: number) => void
  onSelectColor: (color: string) => void
  onBack:        () => void
}) {
  const { t }   = useTranslation()
  const sy      = t.symmetry
  const elapsed = useTimer(state.startTime, state.endTime)

  // Only cells that need painting (solution ≠ white background) count toward progress.
  // Non-given cells whose solution is #FFFFFF start "correct" by default, so excluding
  // them prevents the bar from starting at ~50%.
  const colorableCells = useMemo(
    () =>
      solution.reduce(
        (n: number, row: string[], r: number) =>
          n + row.filter((color: string, c: number) => !state.isGiven[r][c] && color !== '#FFFFFF').length,
        0,
      ),
    [solution, state.isGiven],
  )

  const correctFilled = useMemo(
    () =>
      state.userGrid.reduce(
        (n: number, row: string[], r: number) =>
          n + row.filter((color: string, c: number) =>
            !state.isGiven[r][c] && solution[r][c] !== '#FFFFFF' && color === solution[r][c]
          ).length,
        0,
      ),
    [state.userGrid, state.isGiven, solution],
  )

  const progress = colorableCells > 0 ? correctFilled / colorableCells : 0

  const halfCols = state.cols / 2
  const halfRows = state.rows / 2

  // Detect landscape + measure the grid row to compute cell size
  const rootRef    = useRef<HTMLDivElement>(null)
  const gridRowRef = useRef<HTMLDivElement>(null)
  const [isLandscape,   setIsLandscape]   = useState(false)
  const [gridRowSize,   setGridRowSize]   = useState({ w: 0, h: 0 })

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setIsLandscape(width > height && height > 0)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    const el = gridRowRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setGridRowSize({ w: width, h: height })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [isLandscape])   // re-attach when switching orientation

  const PALETTE_W = 64  // palette column width (48px swatch + padding)
  // Portrait: fill width. Landscape: fit the grid row by both height and width.
  const cellSize = isLandscape && gridRowSize.h > 0
    ? Math.min(
        Math.floor(gridRowSize.h / state.rows),
        Math.floor((gridRowSize.w - PALETTE_W) / state.cols),
        60,
      )
    : Math.min(40, Math.floor(320 / state.cols))

  const paletteSwatches = state.palette.map((color) => {
    const isSelected = state.selectedColor === color
    const colorName  = COLOR_NAMES[color] ?? color
    return (
      <div key={color} style={s.swatchWrap}>
        <button
          onClick={() => onSelectColor(color)}
          style={{
            ...s.colorSwatch,
            background:    color,
            outline:       isSelected
              ? `4px solid ${COLORS.green}`
              : `2px solid ${COLORS.borderWarm}`,
            outlineOffset: isSelected ? 2 : 1,
            transform:     isSelected ? 'scale(1.15)' : 'scale(1)',
            boxShadow:     isSelected ? `0 2px 8px rgba(45,106,79,0.35)` : 'none',
          }}
          aria-label={colorName}
          aria-pressed={isSelected}
        />
        <span style={s.swatchLabel}>{colorName}</span>
      </div>
    )
  })

  const grid = (
    <div style={{ display: 'inline-block' }}>
      {Array.from({ length: state.rows }, (_, r) => (
        <div key={r} style={{ display: 'flex' }}>
          {Array.from({ length: state.cols }, (_, c) => {
            const given   = state.isGiven[r][c]
            const color   = state.userGrid[r][c]

            const axisLeft  = !given && c === halfCols && state.cols > 1
            const axisTop   = !given && r === halfRows && state.rows > 1
            const showAxisV = mode !== 'horizontal' && mode !== 'translation-horizontal' && axisLeft
            const showAxisH = mode !== 'vertical'   && mode !== 'translation-vertical'   && axisTop

            return (
              <div
                key={c}
                onClick={() => !given && onPaintCell(r, c)}
                style={{
                  width:       cellSize,
                  height:      cellSize,
                  background:  color,
                  border:      `1px solid ${given ? '#AAAAAA' : '#DDDDDD'}`,
                  borderLeft:  showAxisV ? `3px solid ${COLORS.green}` : undefined,
                  borderTop:   showAxisH ? `3px solid ${COLORS.green}` : undefined,
                  cursor:      given ? 'default' : 'pointer',
                  boxSizing:   'border-box' as const,
                  transition:  'background 0.08s ease',
                  flexShrink:  0,
                }}
              />
            )
          })}
        </div>
      ))}
    </div>
  )

  return (
    <div ref={rootRef} style={s.playRoot}>
      <GamePlayHeader
        title={sy.title}
        stats={[
          { value: state.moves, label: sy.moves },
          { value: elapsed,     label: sy.time },
        ]}
        progress={progress * 100}
        progressNow={correctFilled}
        progressMax={colorableCells}
        onBack={onBack}
        backLabel={sy.backToDashboard}
      />

      {isLandscape ? (
        <div style={s.landscapeBody}>
          {/* Grid + palette fill available height */}
          <div ref={gridRowRef} style={s.landscapeGridRow}>
            {grid}
            <div style={s.paletteLandscape}>{paletteSwatches}</div>
          </div>
          {/* Text below */}
          <div style={s.landscapeText}>
            <p style={s.progressText}>{correctFilled} / {colorableCells} {sy.progress}</p>
            <p style={s.instruction}>
              {state.gameComplete
                ? '✓ ' + (getModeType(mode) === 'translation' ? sy.instructionCompleteTranslation : sy.instructionComplete)
                : (getModeType(mode) === 'translation' ? sy.instructionPaintTranslation : sy.instructionPaint)}
            </p>
          </div>
        </div>
      ) : (
        <main style={s.playMain}>
          <div style={{ overflowX: 'auto', width: '100%', display: 'flex', justifyContent: 'center' }}>
            {grid}
          </div>
          <p style={s.progressText}>{correctFilled} / {colorableCells} {sy.progress}</p>
          <p style={s.instruction}>
            {state.gameComplete
              ? '✓ ' + (getModeType(mode) === 'translation' ? sy.instructionCompleteTranslation : sy.instructionComplete)
              : (getModeType(mode) === 'translation' ? sy.instructionPaintTranslation : sy.instructionPaint)}
          </p>
          <div style={s.paletteRow}>
            <span style={s.paletteLabel}>{sy.colorPalette}</span>
            <div style={s.paletteColors}>{paletteSwatches}</div>
          </div>
        </main>
      )}
    </div>
  )
}

// ── Styles ─────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight:  '100vh',
    background: COLORS.bgPage,
  },

  // Setup
  modeLabel: { fontSize: FONT.size.md,  fontWeight: 700, color: COLORS.textDark },
  modeDesc:  { fontSize: FONT.size.sm,  color: COLORS.textSecondary, fontWeight: 500 },

  previewWrap: {
    display:        'flex',
    justifyContent: 'center',
    paddingTop:     8,
  },

  // Play header
  playRoot: { height: '100vh', background: COLORS.bgPage, display: 'flex', flexDirection: 'column', overflow: 'hidden' },

  // Play main (portrait)
  playMain: {
    flex:           1,
    display:        'flex',
    flexDirection:  'column' as const,
    alignItems:     'center',
    gap:            16,
    padding:        '20px 12px 32px',
  },

  // Landscape layout
  landscapeBody: {
    flex:           1,
    display:        'flex',
    flexDirection:  'column' as const,
    overflow:       'hidden',
    minHeight:      0,
  },
  landscapeGridRow: {
    flex:           1,
    display:        'flex',
    flexDirection:  'row' as const,
    alignItems:     'center',
    justifyContent: 'center',
    gap:            8,
    overflow:       'hidden',
    minHeight:      0,
    padding:        '6px 12px 0',
  },
  landscapeText: {
    flexShrink:     0,
    display:        'flex',
    flexDirection:  'row' as const,
    alignItems:     'center',
    justifyContent: 'center',
    gap:            24,
    padding:        '4px 12px 6px',
  },

  progressText: {
    fontSize:   FONT.size.base,
    color:      COLORS.textSecondary,
    fontWeight: 600,
    margin:     0,
  },

  instruction: {
    fontSize:   FONT.size.md,
    color:      COLORS.textBody,
    fontStyle:  'italic' as const,
    textAlign:  'center' as const,
    margin:     0,
  },

  // Palette — portrait
  paletteRow: {
    display:        'flex',
    alignItems:     'center',
    gap:            12,
    flexWrap:       'wrap' as const,
    justifyContent: 'center',
  },
  paletteLabel: {
    fontSize:   FONT.size.sm,
    fontWeight: 700,
    color:      COLORS.textSecondary,
    minWidth:   55,
  },
  paletteColors: { display: 'flex', gap: 10, flexWrap: 'wrap' as const, alignItems: 'center' },
  // Palette — landscape (vertical column flush against grid)
  paletteLandscape: {
    display:        'flex',
    flexDirection:  'column' as const,
    alignItems:     'center',
    justifyContent: 'center',
    gap:            8,
    flexShrink:     0,
  },
  swatchWrap: {
    display:        'flex',
    flexDirection:  'column' as const,
    alignItems:     'center',
    gap:            4,
  },
  swatchLabel: {
    fontSize:   FONT.size.xs,
    fontWeight: 600,
    color:      COLORS.textSecondary,
    textAlign:  'center' as const,
    lineHeight: 1,
  },
  colorSwatch: {
    width:        48,
    height:       48,
    minWidth:     48,
    minHeight:    48,
    borderRadius: RADIUS.sm,
    cursor:       'pointer',
    transition:   'transform 0.1s ease, outline 0.1s ease, box-shadow 0.1s ease',
    border:       'none',
    padding:      0,
  },
}
